import React, { useState } from "react"
import { faCalendar, faClose, faFilter } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import ModalPortal from "../../components/Modal/ModalPortal"
import { SIZE } from "../../configuration/Sizes"
import THEME from "../../configuration/Theme"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import GetSpace from "../../utils/space"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"

interface CashFlowPeriodFilterPopupProps {
  initialFilters: {
    dateStart: string
    dateEnd: string
  }
  today: Date
  onApplyFilters: (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => void
  onClose: () => void
}

const getRelativeInputDate = (today: Date, days: number) => {
  const date = new Date(today)
  date.setDate(date.getDate() - days)
  return date.toInputDate()
}

export const CashFlowPeriodFilterPopup: React.FC<CashFlowPeriodFilterPopupProps> = ({
  initialFilters,
  today,
  onApplyFilters,
  onClose,
}) => {
  const { t } = useTranslation("pages")

  const [dateStart, setDateStart] = useState(initialFilters.dateStart)

  const [dateEnd, setDateEnd] = useState(initialFilters.dateEnd)

  const hasValidPeriod = Boolean(dateStart && dateEnd && dateStart <= dateEnd)

  const handleApply = () => {
    onApplyFilters({ dateStart, dateEnd })
  }

  const handleRefresh = () => {
    onApplyFilters({ dateStart, dateEnd }, true)
  }

  const handlePeriodShortcut = (startDate: string, endDate: string) => {
    setDateStart(startDate)
    setDateEnd(endDate)
  }

  const shortcutButtonClass =
    "rounded-lg border-2 border-orange-300 bg-white px-3 py-2 text-sm font-extrabold text-slate-800 transition hover:border-orange-400 hover:bg-orange-100"

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("cashflow.period_filter_popup_title")}
              icon={faFilter}
              children={
                <div className={defaultStyle.ButtonsActions}>
                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    icon={faClose}
                    typeButton="button"
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={5} />

            <div className="p-2 flex flex-col gap-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <InputField
                  icon={faCalendar}
                  value={dateStart}
                  type="date"
                  placeholder={t("cashflow.start_date")}
                  animationTypes={AnimationTypes.slideUpIn}
                  animationDuration="0.45s"
                  onChange={(event) => setDateStart(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.input.startDate"
                />

                <InputField
                  icon={faCalendar}
                  value={dateEnd}
                  type="date"
                  placeholder={t("cashflow.end_date")}
                  animationTypes={AnimationTypes.slideUpIn}
                  animationDuration="0.55s"
                  onChange={(event) => setDateEnd(event.target.value)}
                  errorMessage={hasValidPeriod ? undefined : t("cashflow.period_invalid")}
                  descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.input.endDate"
                />
              </div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={shortcutButtonClass}
                    onClick={() => handlePeriodShortcut(today.toInputDate(), today.toInputDate())}
                    data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.handlePeriodShortcutTodayToInputDateTodayToInputDate"
                  >
                    {t("cashflow.period_today")}
                  </button>

                  <button
                    type="button"
                    className={shortcutButtonClass}
                    onClick={() => handlePeriodShortcut(getRelativeInputDate(today, 1), getRelativeInputDate(today, 1))}
                    data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.handlePeriodShortcutGetRelativeInputDateToday1GetRelativeInp"
                  >
                    {t("cashflow.period_yesterday")}
                  </button>

                  <button
                    type="button"
                    className={shortcutButtonClass}
                    onClick={() => handlePeriodShortcut(getRelativeInputDate(today, 7), today.toInputDate())}
                    data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.handlePeriodShortcutGetRelativeInputDateToday7TodayToInputDa"
                  >
                    {t("cashflow.period_last_7_days")}
                  </button>

                  <button
                    type="button"
                    className={shortcutButtonClass}
                    onClick={() => handlePeriodShortcut(getRelativeInputDate(today, 30), today.toInputDate())}
                    data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.handlePeriodShortcutGetRelativeInputDateToday30TodayToInputD"
                  >
                    {t("cashflow.period_last_30_days")}
                  </button>

                  <button
                    type="button"
                    className={shortcutButtonClass}
                    onClick={() => handlePeriodShortcut(getRelativeInputDate(today, 60), today.toInputDate())}
                    data-interaction-description-key="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.handlePeriodShortcutGetRelativeInputDateToday60TodayToInputD"
                  >
                    {t("cashflow.period_last_60_days")}
                  </button>
                </div>
              </Animate>
            </div>

            <div className={defaultStyle.divisionBar}></div>

            <div className={defaultStyle.buttonsController}>
              <Button
                typeButton="button"
                type="primary"
                size="medium"
                bgColor="#EF4444"
                label={t("cashflow.refresh_period_filter")}
                animationTypes={AnimationTypes.slideUpIn}
                animationDuration="0.65s"
                onClick={handleRefresh}
                disabled={!hasValidPeriod}
                descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.refreshPeriodFilter"
              />

              <GetSpace left={5} />

              <Button
                typeButton="button"
                type="primary"
                size="medium"
                bgColor={THEME.colors.buttonBGSaveColor}
                label={t("cashflow.apply_period_filter")}
                animationTypes={AnimationTypes.slideUpIn}
                animationDuration="0.7s"
                onClick={handleApply}
                disabled={!hasValidPeriod}
                descriptionKey="interactionDescriptions:pages.cashFlow.cashFlowPeriodFilterPopup.button.applyPeriodFilter"
              />
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
